import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Eye, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Modal that renders a stage-email template against a canned sample
 * context server-side (so it uses the exact same interpolator as the
 * live dispatch path) and shows both the subject + the sandboxed HTML
 * body in an iframe. Iframe isolates the template's inline styles from
 * the admin UI's Tailwind reset.
 *
 * Props:
 *   - open, onClose
 *   - stageKey (canonical stage id, e.g. "provisioning")
 *   - stageLabel (human label for the header)
 *   - draft   { subject_template, body_html_template } — the unsaved
 *              current editor state; sent to the backend so admins can
 *              iterate without saving first.
 */
export default function StageEmailPreviewModal({
  open, onClose, stageKey, stageLabel, draft,
}) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!open || !stageKey) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setPreview(null);
      try {
        const { data } = await api.post(
          `/commissions/stage-email-rules/${stageKey}/preview`,
          {
            subject_template: draft?.subject_template || "",
            body_html_template: draft?.body_html_template || "",
          },
        );
        if (!cancelled) setPreview(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
        if (!cancelled) onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, stageKey, draft?.subject_template, draft?.body_html_template, onClose]);

  // Write the rendered HTML into the sandboxed iframe once it lands.
  useEffect(() => {
    if (!preview || !iframeRef.current) return;
    const doc = iframeRef.current.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(preview.body_html || "<p style=\"font-family:system-ui;color:#64748b;padding:16px;\">No body content yet.</p>");
    doc.close();
  }, [preview]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm"
      data-testid="stage-email-preview-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              Preview
            </div>
            <h3 className="text-slate-100 text-base font-semibold inline-flex items-center gap-2">
              <Eye className="w-4 h-4 text-cyan-300" />
              {stageLabel} — email preview
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="stage-email-preview-close"
            className="text-slate-400 hover:text-slate-100 p-1 rounded"
            aria-label="Close preview"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-800 bg-slate-950/40">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
            Subject
          </div>
          <div
            className="text-sm text-slate-100 font-mono break-words"
            data-testid="stage-email-preview-subject"
          >
            {loading ? (
              <span className="inline-flex items-center gap-2 text-slate-500">
                <Loader2 className="w-3 h-3 animate-spin" /> Rendering…
              </span>
            ) : (
              preview?.subject || <span className="italic text-slate-500">(empty subject)</span>
            )}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden bg-slate-100">
          {loading ? (
            <div className="w-full h-full flex items-center justify-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : (
            <iframe
              ref={iframeRef}
              title="Stage email preview"
              className="w-full h-[420px] bg-white"
              data-testid="stage-email-preview-iframe"
              sandbox=""
            />
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-800 text-[11px] text-slate-500 flex items-center justify-between gap-3">
          <span>
            Rendered with <b>sample data</b> — real sends interpolate the actual customer, partner, and plan.
          </span>
          <button
            type="button"
            onClick={onClose}
            data-testid="stage-email-preview-done"
            className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 text-xs"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
